import React, { useState, useEffect } from "react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { useNavigate } from "react-router-dom"

import * as Commands from "../../../../../model/commands"
import { ID, WebSource, Idea } from "../../../../../model/types"
import SourceAutocomplete from "./source_autocomplete"
import { useNana } from "../../../../hooks/useNana"
import { AutocompleteResult } from "../../../../components/autocomplete"
import { SourcesPageState } from "../../sources_page"
import { generatePublicId } from "../../../../../model/public_id"

const metaLabelClass =
  "w-16 shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-stone-400 dark:text-stone-500"

const pillButtonClass =
  "inline-flex h-7 shrink-0 cursor-pointer items-center rounded-full px-2.5 text-[12px] font-medium text-stone-500 transition-colors hover:bg-stone-900/[0.06] hover:text-stone-800 dark:text-stone-400 dark:hover:bg-white/[0.08] dark:hover:text-stone-100"

const ShowSource = ({ idea }: { idea: Idea }) => {
  const { model, commander, hub } = useNana()
  const [sourceType, setSourceType] = useState<string | null>(idea.sourceType)
  const [sourceId, setSourceId] = useState<ID | null>(idea.sourceId)
  const [tick, setTick] = useState(0)
  const navigate = useNavigate()

  const webSource = sourceId ? model.getSource(sourceId) : null

  useEffect(() => {
    const keys = [
      hub.on("model:idea-updated", ({ id, idea, fields }) => {
        if (id === idea.id && fields.hasOwnProperty("sourceId")) {
          setSourceType(idea.sourceType)
          setSourceId(idea.sourceId)
        }
      }),
      hub.on("model:source-updated", ({ id }) => {
        if (id == sourceId) setTick((t) => t + 1)
      }),
    ]

    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [setSourceType, setSourceId, idea])

  useEffect(() => {
    setSourceType(idea.sourceType)
    setSourceId(idea.sourceId)
  }, [idea.id])

  if (idea.type !== "idea") return null

  const setSourceToSelf = () => {
    setSourceType("self")
    setSourceId(null)
    commander.exec(
      new Commands.SetIdeaSourceCommand({
        id: idea.id,
        sourceType: "self",
        sourceId: null,
      })
    )
  }

  const disconnectFromSource = () => {
    setSourceType(null)
    setSourceId(null)
    commander.exec(
      new Commands.SetIdeaSourceCommand({
        id: idea.id,
        sourceType: null,
        sourceId: null,
      })
    )
  }

  const connectToSource = (
    result: AutocompleteResult<WebSource>,
    autocompleteFilter: string
  ): boolean => {
    if (result.type === "none") return false

    const source = result.item
    if (source) {
      const { id } = source
      commander.exec(
        new Commands.SetIdeaSourceCommand({
          id: idea.id,
          sourceType: "web",
          sourceId: id,
        })
      )
      setSourceType("web")
      setSourceId(source.id)
      return true
    } else {
      const pattern = new RegExp("^(https?:\\/\\/).*")
      if (pattern.test(autocompleteFilter)) {
        let sourceId: ID = generatePublicId()
        const url = new URL(autocompleteFilter)
        const existingSource = model.getSourceByURL(url)
        if (!existingSource) {
          commander.exec(
            new Commands.CreateWebSourceCommand({
              id: sourceId,
              source: {
                id: sourceId,
                url: autocompleteFilter,
                status: "new",
                createdAt: new Date(),
                updatedAt: new Date(),
                lastUsedAt: new Date(),
              },
              relatedIdeaIds: [],
              fleetingIdeas: [],
            })
          )
        } else {
          sourceId = existingSource.id
        }
        commander.exec(
          new Commands.SetIdeaSourceCommand({
            id: idea.id,
            sourceType: "web",
            sourceId,
          })
        )
        setSourceType("web")
        setSourceId(sourceId)
        return true
      }
    }
    return false
  }

  return (
    <div className="min-h-10 px-5 py-2 md:px-7">
      {sourceType === "self" && (
        <div className="flex items-center justify-between gap-3 text-sm">
          <div className="flex min-w-0 items-center gap-3">
            <div className={metaLabelClass}>Source</div>
            <div className="text-stone-700 dark:text-stone-300">Myself</div>
          </div>
          <button
            type="button"
            id="disconnect-source"
            onClick={() => disconnectFromSource()}
            className="flex h-7 w-7 items-center justify-center rounded-full text-stone-400 transition-colors hover:bg-stone-900/[0.06] hover:text-stone-700 dark:hover:bg-white/[0.08] dark:hover:text-stone-200"
            title="Remove source"
            aria-label="Remove source"
          >
            <XMarkIcon className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      )}
      {sourceType === "web" && webSource && (
        <div className="flex items-center justify-between gap-3 text-sm">
          <div className="flex min-w-0 items-center gap-3">
            <div className={metaLabelClass}>Source</div>
            <button
              type="button"
              onClick={() => {
                if (model) SourcesPageState.openSourceId = webSource.id
                navigate(`/app/sources`)
              }}
              className="min-w-0 truncate text-left text-stone-700 transition-colors hover:text-sky-700 dark:text-stone-300 dark:hover:text-sky-300"
            >
              {webSource.name || webSource.url}
            </button>
          </div>
          <button
            type="button"
            id="disconnect-source"
            onClick={() => disconnectFromSource()}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-stone-400 transition-colors hover:bg-stone-900/[0.06] hover:text-stone-700 dark:hover:bg-white/[0.08] dark:hover:text-stone-200"
            title="Remove source"
            aria-label="Remove source"
          >
            <XMarkIcon className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      )}
      {sourceType == null && (
        <div className="flex items-center gap-3 text-sm">
          <div className={metaLabelClass}>Source</div>
          <div className="min-w-0 flex-1">
            <SourceAutocomplete connect={connectToSource} />
          </div>
          <button
            type="button"
            id="set-source-to-self"
            onClick={() => setSourceToSelf()}
            className={pillButtonClass}
          >
            Self
          </button>
        </div>
      )}
    </div>
  )
}

export default ShowSource
